import { Link } from 'react-router-dom';
import { Mock } from '../components/Mock';
import { Notice, Section } from '../components/Section';
import { ShareButtons } from '../components/ShareButtons';
import { ThankYou } from '../components/ThankYou';
import { googleCalendarUrl, icsDataUrl, type CalendarEvent } from '../lib/calendar';
import { stayDate } from '../lib/format';
import { usePageMeta } from '../lib/usePageMeta';
import { useSettings } from '../lib/useSettings';
import { EVENT, SEO } from '../seo/pages';

/**
 * Rewritten on 2026-09-15, when the celebration moved to Secrets Hideaway.
 *
 * What the family has given us is the whole of what this page may say about
 * the night: in through the Tiki bar on the white-fence side, free entry from 7
 * to 10 PM, a wristband on arrival, food, and another wristband from the Front
 * Desk for anyone staying past ten. The old hour-by-hour schedule (doors at six,
 * dinner nine to eleven, Sunday breakfast and a group photo) belonged to the
 * hotel weekend and is gone with it - none of it has been restated here in a new
 * form. Still no closing time: James confirmed on 2026-09-15 that the night does
 * not end at ten and has never said when it does. The wristband after ten is
 * paid (Eric, 2026-09-15) and the page says so, because a guest handed a free
 * wristband at the door will otherwise assume the second one is free too and
 * find out at the desk. No price is given, because none has been. See
 * docs/COPY.md.
 */
export function EventPage() {
  usePageMeta(SEO.event.title, SEO.event.description, { jsonLd: SEO.event.jsonLd });
  const { data: s } = useSettings();
  const venue = EVENT.venue.name;
  const address = `${EVENT.venue.street}, ${EVENT.venue.city}, ${EVENT.venue.region} ${EVENT.venue.postalCode}`;
  const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
  // Only the start date: one evening, and a stale eventEndDate in settings must
  // not put the old Sunday back on the page.
  const when = s?.eventStartDate ? stayDate(s.eventStartDate) : 'Date coming soon';
  const link = 'text-pink-300 underline hover:text-pink-200';
  const calendarEvent: CalendarEvent | null = s?.eventStartDate
    ? {
        title: s.eventName || 'Celebration of Life for Brandon',
        start: s.eventStartDate,
        end: s.eventStartDate,
        location: [venue, address].filter(Boolean).join(', ') || null,
        description:
          'Free entry 7 to 10 PM through the Tiki bar entrance, with a wristband on arrival, and the night carries on after that. Details and where to stay at brandonstrong.com',
        url: 'https://brandonstrong.com/event',
      }
    : null;

  return (
    <>
      <Section eyebrow="The celebration" title={s?.eventName ?? 'Celebration of Life'}>
        <div className="-mt-4 mb-10 max-w-3xl">
          <p className="text-xl text-ink-100">
            An evening for Brandon at {venue}. Come in through the Tiki bar between 7:00 and 10:00
            PM. Entry is free, you will be given a wristband on the way in, and there will be food.
            The night does not end at ten. Come dressed for the show.
          </p>
          <div className="mt-6">
            <Notice tone="warning">
              The celebration moved here from the hotel, and the room block went with it.{' '}
              <Link to="/update" className={link}>
                Read the family&apos;s update
              </Link>
              . It covers the move, the rooms and the refunds.
            </Notice>
          </div>
        </div>

        <div className="grid gap-10 lg:grid-cols-[1fr_1fr]">
          <dl className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1">
            <div>
              <dt className="text-meta text-sm">When</dt>
              <dd className="mt-1 text-lg text-ink-50">
                {when}
                <span className="block text-sm text-ink-400">
                  Free entry 7:00 to 10:00 PM, and the night carries on after that.
                </span>
                {calendarEvent && (
                  <span className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm">
                    <a
                      href={icsDataUrl(calendarEvent)}
                      download="brandonstrong-celebration.ics"
                      className={link}
                    >
                      Add to calendar
                    </a>
                    <a
                      href={googleCalendarUrl(calendarEvent)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={link}
                    >
                      Google Calendar
                    </a>
                  </span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-meta text-sm">Where</dt>
              <dd className="mt-1 text-lg text-ink-50">
                {venue}
                <span className="block text-sm text-ink-400">{address}</span>
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-sm ${link}`}
                >
                  Get directions
                </a>
              </dd>
            </div>
            <div>
              <dt className="text-meta text-sm">Getting in</dt>
              <dd className="mt-1 text-lg text-ink-50">
                Through the Tiki bar, by the white fence
                <span className="block text-sm text-ink-400">
                  The way in for Brandon&apos;s Celebration of Life is on the Tiki Bar side, at the
                  white fence, separate from the rest of the venue, and it is the one to use.
                  Everyone coming for Brandon is given a wristband on the way in, and security will
                  be around to point people the right way.
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-meta text-sm">Dress code</dt>
              <dd className="mt-1 text-lg text-ink-50">
                Rockstar.
                <Mock className="mt-2">
                  <p>
                    Come dressed for the show, not the service. Leather and denim, band tees, boots,
                    studs, sunglasses at night, big hair, glitter if you dare. Black is fine if it
                    is a band shirt; leave the suits and the ties at home. Save the loudest outfit
                    for seven o&apos;clock, and tag your photos #BrandonStrong.
                  </p>
                </Mock>
              </dd>
            </div>
            <div>
              <dt className="text-meta text-sm">Staying over</dt>
              <dd className="mt-1 text-lg text-ink-50">
                {venue} is sold out
                <span className="block text-sm text-ink-400">
                  Overflow rooms at hotels a few minutes away are on{' '}
                  <Link to="/rooms" className={link}>
                    Where to stay
                  </Link>
                  , booked through SecretsOverflow.
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-meta text-sm">RSVP</dt>
              <dd className="mt-1 text-lg text-ink-50">
                Please{' '}
                <Link to="/rsvp" className={link}>
                  RSVP
                </Link>{' '}
                with the name of everyone in your party
                <span className="block text-sm text-ink-400">
                  That is the list at the door. A donation is welcome, never required, and all of it
                  goes to Shawna.
                </span>
              </dd>
            </div>
          </dl>

          {/* Bob's corrected flier, 2026-09-15: Secrets Hideaway, doors at seven,
              free 7 to 10, Tiki Bar entrance. The file is served from public/ so
              the download link is a plain URL. */}
          <figure className="flex flex-col gap-4">
            <img
              src="/flier.jpg"
              width={1024}
              height={1536}
              alt="Flier: Let's Rock n Roll in honor of Brandon, Celebration of Life, Sat Oct 3. Free event 7PM to 10PM, doors open at 7PM, Tiki Bar entrance. DJs performing: DRect, Just James, TaylorTime, Blake Blaze, Mike Drop, DVS. Secrets Hideaway Resort and Spa, 2145 E Irlo Bronson Memorial Hwy, Kissimmee, FL 34744. brandonstrong.com"
              className="w-full border border-white/10"
            />
            <figcaption className="flex flex-wrap items-center gap-3 text-sm text-ink-400">
              <a href="/flier.jpg" download="brandonstrong-flier.jpg" className="btn-outline">
                Download the flier
              </a>
              <span>Save it, print it, send it.</span>
            </figcaption>
          </figure>
        </div>

        <div className="mt-12 grid gap-10 md:grid-cols-2">
          <div>
            <h2 className="text-3xl">Getting there</h2>
            <ul className="mt-4 flex flex-col gap-3 text-ink-200">
              <li>
                {venue} is at {address}. The Get directions link above opens the route in Google
                Maps.
              </li>
              <li>
                Head for the Tiki bar rather than the main entrance. The way in is on that side, at
                the white fence. Give your name at the door, and security will be around to guide
                people in.
              </li>
              <li>
                Coming from out of town and need a bed?{' '}
                <Link to="/rooms" className={link}>
                  Where to stay
                </Link>{' '}
                has the nearest hotels; the closest is about a third of a mile away.
              </li>
            </ul>
          </div>
          <div>
            <h2 className="text-3xl">Good to know</h2>
            <ul className="mt-4 flex flex-col gap-3 text-ink-200">
              <li>
                Entry is free between 7:00 and 10:00 PM for everyone here for Brandon, and the
                wristband you are given on the way in is for the celebration.
              </li>
              <li>
                The night does not end at ten. Free entry does: staying on after that means a
                wristband from the Front Desk, which is not free.
              </li>
              <li>There will be food.</li>
              <li>
                If you booked a room through this site for the old hotel, that reservation is being
                refunded and there is nothing you need to do.
              </li>
              <li>
                Anything else: see the{' '}
                <Link to="/contact" className={link}>
                  FAQ
                </Link>
                , or email{' '}
                <a href={`mailto:${EVENT.questionsEmail}`} className={link}>
                  {EVENT.questionsEmail}
                </a>
                .
              </li>
            </ul>
          </div>
        </div>

        <ShareButtons
          className="mt-10"
          title={s?.eventName ?? 'Celebration of Life for Brandon'}
          text={`Join us at the celebration of life for Brandon${when !== 'Dates coming soon' ? `, ${when}` : ''}, at ${venue}.`}
        />
      </Section>

      <ThankYou />
    </>
  );
}
